import { useState, useEffect, useRef } from 'react'
import api from '../services/api'

const TIPOS = [
  { value: 'NFE', label: 'NF-e' },
  { value: 'CTEOS', label: 'CT-e OS' },
  { value: 'NFSE', label: 'NFS-e (Municipal e Nacional)' },
]

interface Cliente {
  cli_cod: string; cliente: string; cidade: string | null; estado: string | null; vlr_contabil: string
}
interface Empresa { empcod: number; empnom: string; empfan: string; empcgc: string }

function hoje() { return new Date().toISOString().slice(0, 10) }
function primeiroDiaMes() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` }

function fmtMoeda(v: string | number) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// ── Combo com checkboxes (multi-seleção) ──────────────────────────────────────
function ComboCheck({ label, opcoes, selecionados, onChange, placeholder, width }: {
  label: string
  opcoes: { value: string; label: string }[]
  selecionados: string[]
  onChange: (v: string[]) => void
  placeholder: string
  width?: number
}) {
  const [aberto, setAberto] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function toggle(v: string) {
    onChange(selecionados.includes(v) ? selecionados.filter(x => x !== v) : [...selecionados, v])
  }

  const resumo = selecionados.length === 0
    ? placeholder
    : selecionados.length === opcoes.length
      ? 'Todas'
      : selecionados.length === 1
        ? opcoes.find(o => o.value === selecionados[0])?.label ?? '1 selecionada'
        : `${selecionados.length} selecionadas`

  return (
    <div className="field" style={{ position: 'relative', width: width ?? 200, flex: '0 0 auto' }} ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        onClick={() => setAberto(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #d1d5db',
          borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: selecionados.length ? '#111827' : '#9ca3af',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumo}</span>
        <span style={{ color: '#9ca3af', marginLeft: 6 }}>{aberto ? '▴' : '▾'}</span>
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 2,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          maxHeight: 260, overflowY: 'auto', boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
            <button type="button" onClick={() => onChange(opcoes.map(o => o.value))}
              style={{ fontSize: '0.72rem', color: '#1a6ea8', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Marcar todas
            </button>
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize: '0.72rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
              Limpar
            </button>
          </div>
          {opcoes.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={selecionados.includes(o.value)} onChange={() => toggle(o.value)}
                style={{ width: 15, height: 15, flexShrink: 0, margin: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RelatorioFaturamentoCliente() {
  const [dataIni, setDataIni] = useState(primeiroDiaMes())
  const [dataFim, setDataFim] = useState(hoje())
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [tiposSel, setTiposSel] = useState<string[]>([])

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [clientes, setClientes] = useState<Cliente[] | null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => setEmpresas(r.data)).catch(() => {})
  }, [])

  function filtrosParams() {
    return {
      dataIni, dataFim,
      empresas: empresasSel.join(','),
      tipos: tiposSel.join(','),
    }
  }

  async function gerar() {
    setCarregando(true)
    setErro('')
    setClientes(null)
    try {
      const r = await api.get('/relatorios/faturamento-cliente', { params: filtrosParams() })
      setClientes(r.data)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/faturamento-cliente/pdf', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (e: any) {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/faturamento-cliente/excel', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'relatorio-faturamento-cliente.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const totalGeral = (clientes ?? []).reduce((s, c) => s + Number(c.vlr_contabil || 0), 0)

  const th: React.CSSProperties = { padding: '0.65rem 0.8rem', textAlign: 'right', color: '#fff', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }
  const thLeft: React.CSSProperties = { ...th, textAlign: 'left' }
  const td: React.CSSProperties = { padding: '0.65rem 0.8rem', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap' }
  const tdLeft: React.CSSProperties = { ...td, textAlign: 'left' }

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.1rem', color: '#1a6ea8' }}>
          Faturamento por Cliente
        </h2>

        <div style={{ background: '#f7faff', borderLeft: '4px solid #1a6ea8', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-start' }}>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão de</label>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} />
            </div>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão até</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
            </div>
            <ComboCheck
              label="Empresas"
              placeholder="Todas"
              width={200}
              opcoes={empresas.map(e => ({ value: String(e.empcod), label: `${e.empcod} — ${e.empfan || e.empnom}` }))}
              selecionados={empresasSel}
              onChange={setEmpresasSel}
            />
            <ComboCheck
              label="Tipo de NF"
              placeholder="Todos"
              width={210}
              opcoes={TIPOS}
              selecionados={tiposSel}
              onChange={setTiposSel}
            />
          </div>
        </div>

        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '1.2rem' }}>
          Traz apenas notas autorizadas, agrupadas por cliente.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>
            {carregando ? 'Gerando...' : 'Gerar relatório'}
          </button>
          {clientes && (
            <button
              type="button"
              onClick={gerarPdf}
              disabled={gerandoPdf}
              style={{
                padding: '0.5rem 1rem', background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoPdf ? 'not-allowed' : 'pointer',
                opacity: gerandoPdf ? 0.7 : 1,
              }}
            >
              {gerandoPdf ? 'Gerando PDF...' : 'PDF'}
            </button>
          )}
          {clientes && (
            <button
              type="button"
              onClick={gerarExcel}
              disabled={gerandoExcel}
              style={{
                padding: '0.5rem 1rem', background: '#15803d', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoExcel ? 'not-allowed' : 'pointer',
                opacity: gerandoExcel ? 0.7 : 1,
              }}
            >
              {gerandoExcel ? 'Gerando Excel...' : 'Excel'}
            </button>
          )}
        </div>
        {erro && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{erro}</p>}
      </div>

      {clientes && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Clientes</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a6ea8' }}>{clientes.length}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Geral</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#15803d' }}>R$ {fmtMoeda(totalGeral)}</div>
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#1a6ea8' }}>
                  <th style={thLeft}>Cliente</th>
                  <th style={thLeft}>Cidade</th>
                  <th style={thLeft}>Estado</th>
                  <th style={th}>Vlr. Contábil</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c, i) => (
                  <tr key={c.cli_cod} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #eef0f4' }}>
                    <td style={{ ...tdLeft, color: '#111827', fontWeight: 500 }}>{c.cli_cod} — {c.cliente}</td>
                    <td style={tdLeft}>{c.cidade || '-'}</td>
                    <td style={tdLeft}>{c.estado || '-'}</td>
                    <td style={{ ...td, color: '#15803d', fontWeight: 600 }}>{fmtMoeda(c.vlr_contabil)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #1a6ea8', background: '#f7faff', fontWeight: 700 }}>
                  <td colSpan={3} style={{ padding: '0.75rem 0.8rem', textAlign: 'right', color: '#1a6ea8' }}>TOTAL GERAL:</td>
                  <td style={{ ...td, color: '#15803d', fontWeight: 700 }}>{fmtMoeda(totalGeral)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
